"use client";

import { PlusIcon } from "lucide-react";

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import FaqAnswerText from "@/components/faq/FaqAnswerText";
import { cn } from "@/lib/utils";
import type { PublicFaqItem } from "@/lib/faq-public";
import { ga4CtaClick } from "@/lib/analytics/ga4-ecommerce";

type Props = {
	items: PublicFaqItem[];
	className?: string;
	/** GA4 `cta_location` when a FAQ question is opened. */
	ctaLocation?: string;
};

export default function PublicFaqAccordion({
	items,
	className,
	ctaLocation = "faq_accordion",
}: Props) {
	if (items.length === 0) return null;

	return (
		<Accordion
			type="single"
			collapsible
			className={cn("w-full max-w-xl space-y-6", className)}
			onValueChange={(value) => {
				if (!value) return;
				const item = items.find((faq) => faq.id === value);
				ga4CtaClick({
					cta_id: `faq_open_${String(item?.id ?? value)
						.trim()
						.toLowerCase()
						.replace(/[^a-z0-9]+/g, "_")
						.replace(/^_|_$/g, "")
						.slice(0, 48) || "item"}`,
					cta_name: item?.question?.slice(0, 100) || "FAQ",
					cta_location: ctaLocation,
				});
			}}
		>
			{items.map(({ id, question, answer }) => (
				<AccordionItem key={id} value={id}>
					<AccordionTrigger
						className="!text-sm"
						icon={
							<PlusIcon
								strokeWidth={2}
								className="size-6 shrink-0 transition-transform duration-300 group-[[data-state=open]]:rotate-45"
							/>
						}
					>
						{question}
					</AccordionTrigger>
					<AccordionContent>
						<FaqAnswerText
							answer={answer}
							className="min-h-24 p-4 text-sm font-light text-foreground sm:px-8 sm:text-sm2"
						/>
					</AccordionContent>
				</AccordionItem>
			))}
		</Accordion>
	);
}
